// YouScreen.tsx — "You". Quiet, grouped settings. Off-by-default reminders,
// privacy-first, and a clear way to clear on-device progress.
import React from 'react';
import { Display, Eyebrow, Meta, Pressable } from '@/components/ui';
import { Icon, type IconName, Wordmark } from '@/components/icons';
import { Capacitor } from '@capacitor/core';
import { AddToHomeScreenSheet } from '@/components/AddToHomeScreenSheet';
import { OrgCodeDialog } from '@/components/OrgCodeDialog';
import { openExternal } from '@/services/openExternal';
import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/hooks/useAuth';
import { canSync, postResearchConsent, openBillingPortal, openResearchCheckin, getStripeSubscriptionStatus, type StripeSubscriptionStatus } from '@/services/sync';
import { subscriptionRowState } from '@/services/subscriptionRow';
import { logConsent } from '@/services/consent';
import { CONSENT_VERSIONS } from '@/config/consent';
import { AccountSignIn } from '@/components/AccountSignIn';
import { PURCHASE_URL, SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';
import { openPaywall } from '@/services/paywallStore';
import { manageSubscriptionUrl, storeDiagnostics } from '@/services/purchases';
import { Browser } from '@capacitor/browser';
import { exportJournals, parseBackup } from '@/services/journalBackup';
import { InAppJournalingDialog } from '@/flows/journaling/InAppJournalingDialog';
import { DeleteAccountDialog } from '@/components/DeleteAccountDialog';
import { CancelSubscriptionDialog, formatAccessDate } from '@/components/CancelSubscriptionDialog';
import { BugReportDialog } from '@/components/BugReportDialog';
import { IN_APP_JOURNALING } from '@/config/features';
import { GuidePreview } from '@/flows/guide/GuidePreview';
import { ReconsentDialog } from '@/flows/research/ReconsentDialog';

const openUrl = (url: string) => openExternal(url);

// The whole developer / content-review section is collapsed behind this PIN, so a
// curious user (or someone on a shared device) can't reach the dev + review tools.
// Entered once to unfold the section for this visit; leaving Settings re-locks it.
const DEV_PIN = '8008';

export function YouScreen({ onClose, onOpenReview, focus }: { onClose?: () => void; onOpenReview?: () => void; focus?: 'reminders' | null }) {
  const progress = useProgress();
  const auth = useAuth();
  // When opened from the Today reminder tile, scroll straight to Reminders.
  const remindersRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (focus !== 'reminders') return;
    const t = setTimeout(() => remindersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 140);
    return () => clearTimeout(t);
  }, [focus]);
  const reviewCount = progress.state.reviewComments.length;
  const [confirmSignOut, setConfirmSignOut] = React.useState(false);
  const [confirmInApp, setConfirmInApp] = React.useState(false);
  const [showDelete, setShowDelete] = React.useState(false);
  const [showA2HS, setShowA2HS] = React.useState(false);
  const [showOrgCode, setShowOrgCode] = React.useState(false);
  // "Add to home screen" only makes sense on mobile WEB (not the native app, not
  // desktop, not when already launched from the home screen). Lets a user who
  // dismissed or missed the Today tile reach the guide again.
  const a2hsRelevant = (() => {
    if (Capacitor.isNativePlatform() || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isMobile = /iPad|iPhone|iPod|Android/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
    const standalone =
      (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    return isMobile && !standalone;
  })();
  const [showBug, setShowBug] = React.useState(false);
  // Turning research ON re-presents the full onboarding consent page; consent is
  // only recorded if they confirm there.
  const [showReconsent, setShowReconsent] = React.useState(false);
  // PIN gate for the whole Developer section. Entering the PIN unfolds it for this
  // visit (devUnlocked); the toggles inside then flip freely. Leaving Settings
  // (unmount) re-locks it, so the tools can't be left open on a shared device.
  const [pinOpen, setPinOpen] = React.useState(false);
  const [pinValue, setPinValue] = React.useState('');
  const [pinError, setPinError] = React.useState(false);
  const [devUnlocked, setDevUnlocked] = React.useState(false);
  const [storeInfo, setStoreInfo] = React.useState<string | null>(null);
  const requestUnlock = () => { setPinValue(''); setPinError(false); setPinOpen(true); };
  const submitPin = () => {
    if (pinValue !== DEV_PIN) { setPinError(true); return; }
    setDevUnlocked(true);
    setPinOpen(false); setPinValue('');
  };
  const [importMsg, setImportMsg] = React.useState<string | null>(null);
  const [showCancel, setShowCancel] = React.useState(false);
  // undefined = still checking, null = the check failed, object = the server's answer.
  const [subStatus, setSubStatus] = React.useState<StripeSubscriptionStatus | null | undefined>(undefined);
  const [billingBusy, setBillingBusy] = React.useState(false);
  const [billingErr, setBillingErr] = React.useState<string | null>(null);

  const onManageBilling = async () => {
    const token = auth.session?.accessToken;
    if (!token || token === 'local' || billingBusy) return;
    setBillingBusy(true); setBillingErr(null);
    const res = await openBillingPortal(token);
    setBillingBusy(false);
    if (!res.ok) setBillingErr(res.error || 'Could not open the billing portal.');
  };
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const inApp = IN_APP_JOURNALING && progress.state.inAppJournaling;
  const daily = progress.state.morningAnchorTime; // the main daily reminder
  const calm = progress.state.calmPracticeTime; // optional extra Calm practice reminder
  // On web we save the reminder preference but cannot reliably fire a notification
  // (no background scheduling), so be honest rather than imply a nudge will arrive.
  const remindersNotify = Capacitor.isNativePlatform();
  const entryCount = Object.values(progress.state.reflections).filter((r) => r.body.trim().length > 0).length;

  // Account: who's signed in and their entitlement (member / free trial / free).
  const acct = auth.session;
  // The single subscription row (see render). null => nothing to show for this source.
  const subSource = auth.entitlementSource;
  const sessionToken = acct?.accessToken;
  const canCancelInApp = !!sessionToken && sessionToken !== 'local';
  const accessDate = formatAccessDate(subStatus?.accessUntil ?? acct?.currentPeriodEnd);
  // Ask the server once per screen open (per token) for stripe members of any plan.
  const statusToken = subSource === 'stripe' && canCancelInApp ? sessionToken : undefined;
  const statusVersion = React.useRef(0);
  React.useEffect(() => {
    setSubStatus(undefined);
    if (!statusToken) return;
    let live = true;
    const v = ++statusVersion.current;
    getStripeSubscriptionStatus(statusToken).then((r) => { if (live && statusVersion.current === v) setSubStatus(r); });
    return () => { live = false; };
  }, [statusToken]);
  const keepNote = "You keep access until the end of the period you’ve paid for.";
  const rowKind = subscriptionRowState(subSource, acct?.plan, statusToken ? subStatus : null, canCancelInApp);
  const subRow: { title: string; detail: string; onClick?: () => void; external?: boolean } | null =
    rowKind === 'store' && (subSource === 'apple' || subSource === 'google')
      ? {
          title: 'Cancel subscription',
          detail: `Subscriptions bought through ${subSource === 'apple' ? 'the App Store' : 'Google Play'} are cancelled in ${subSource === 'apple' ? 'the App Store' : 'Google Play'}. ${keepNote}`,
          onClick: () => { Browser.open({ url: manageSubscriptionUrl(subSource) }).catch(() => undefined); },
          external: true,
        }
      : rowKind === 'cancel'
        ? {
            title: 'Cancel subscription',
            detail: 'Stop your monthly payments. You keep access until the end of the period you’ve paid for.',
            onClick: canCancelInApp ? () => setShowCancel(true) : undefined,
          }
        : rowKind === 'cancelled'
          ? {
              title: 'Subscription cancelled',
              detail: accessDate ? `Subscription cancelled. You have access until ${accessDate}.` : `Subscription cancelled. ${keepNote}`,
            }
          : rowKind === 'no-renew'
            ? {
                title: 'Membership',
                detail: `Your membership doesn’t renew, so there’s nothing to cancel.${accessDate ? ` It runs until ${accessDate}.` : ''}`,
              }
            : rowKind === 'loading'
              ? { title: 'Membership', detail: 'Checking your subscription…' }
              : rowKind === 'check-failed'
                ? { title: 'Membership', detail: 'We couldn’t check your subscription just now. Please try again in a moment.' }
                : rowKind === 'active'
                  ? { title: 'Membership', detail: 'Your membership is active.' }
                  : rowKind === 'org'
                  ? { title: 'Membership', detail: 'Your access is provided by your organisation, so there’s nothing to cancel.' }
                  : null;
  const acctTitle = acct ? acct.user.email || 'Signed in' : null;
  const acctStatus = acct
    ? auth.isMember
      ? 'Member · full access'
      : auth.trialActive
        ? `Free trial · ${auth.trialDaysLeft ?? 10} ${auth.trialDaysLeft === 1 ? 'day' : 'days'} left`
        : 'Free plan'
    : null;

  const doExport = () => {
    setImportMsg(null);
    exportJournals(progress.state.reflections, new Date().toISOString()).catch(() =>
      setImportMsg('Could not create the backup file.'),
    );
  };
  const onImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const entries = parseBackup(String(reader.result || ''));
        progress.importReflections(entries);
        const n = Object.keys(entries).length;
        setImportMsg(`Imported ${n} ${n === 1 ? 'entry' : 'entries'} from your backup.`);
      } catch (err) {
        setImportMsg(err instanceof Error ? err.message : 'That file could not be imported.');
      }
    };
    reader.onerror = () => setImportMsg('That file could not be read.');
    reader.readAsText(file);
  };

  const group: React.CSSProperties = {
    borderRadius: 'var(--r-card)',
    overflow: 'hidden',
    marginTop: 11,
    background: 'linear-gradient(178deg, var(--card-top), var(--card-bot))',
    border: '1px solid var(--hair)',
    boxShadow: 'var(--edge), var(--sh1)',
  };
  const label: React.CSSProperties = {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--ink-soft)',
    margin: '22px 4px 0',
  };
  const timeInput: React.CSSProperties = {
    padding: '7px 10px',
    borderRadius: 9,
    border: '1px solid var(--hair)',
    background: 'var(--card-top)',
    color: 'var(--ink)',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 14,
  };

  const Row = ({
    icon,
    title,
    detail,
    last,
    control,
    onClick,
    danger,
  }: {
    icon: IconName;
    title: string;
    detail?: string;
    last?: boolean;
    control?: React.ReactNode;
    onClick?: () => void;
    danger?: boolean;
  }) => (
    <Pressable
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        padding: '13px 15px',
        minHeight: 52,
        borderBottom: last ? 'none' : '1px solid var(--hair)',
      }}
    >
      <span style={{ color: danger ? 'var(--amber-deep)' : 'var(--amber-deep)', flexShrink: 0 }}>
        <Icon name={icon} size={19} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, color: danger ? 'var(--amber-deep)' : 'var(--ink)' }}>
          {title}
        </div>
        {detail && <Meta style={{ marginTop: 2, display: 'block' }}>{detail}</Meta>}
      </div>
      {/* Only show the chevron when the row actually does something. An info-only
          row (no onClick, no control) renders nothing on the right, so it never
          looks tappable when it isn't. */}
      {control ?? (onClick ? (
        <span style={{ color: 'var(--ink-soft)', opacity: 0.45 }}>
          <Icon name="chevRight" size={15} />
        </span>
      ) : null)}
    </Pressable>
  );

  const Toggle = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
    <Pressable
      as="button"
      onClick={onClick}
      ariaLabel="Toggle weekly note"
      style={{
        width: 46,
        height: 27,
        borderRadius: 999,
        border: 'none',
        background: on ? 'linear-gradient(180deg,#2BB184,var(--green))' : 'var(--hair-strong)',
        position: 'relative',
        flexShrink: 0,
        boxShadow: on ? 'inset 0 1px 0 rgba(255,255,255,0.3)' : 'inset 0 1px 2px rgba(0,0,0,0.15)',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: on ? 22 : 3,
          width: 21,
          height: 21,
          borderRadius: 999,
          background: '#fff',
          transition: 'left .2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        }}
      />
    </Pressable>
  );

  return (
    <>
    <div style={{ position: 'relative', minHeight: '100%', padding: 'calc(16px + var(--safe-top)) 18px calc(40px + var(--safe-bottom))', maxWidth: 560, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <Eyebrow style={{ marginBottom: 9 }}>Settings</Eyebrow>
          <Display parts={{ pre: 'Your ', em: 'pace', post: '.' }} size={25} />
        </div>
        {onClose && (
          <Pressable
            as="button"
            onClick={onClose}
            ariaLabel="Close settings"
            style={{ width: 44, height: 44, borderRadius: 999, border: '1px solid var(--hair)', background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--edge), var(--sh1)', flexShrink: 0 }}
          >
            <Icon name="chevDown" size={20} />
          </Pressable>
        )}
      </div>
      <div
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontWeight: 300,
          fontStyle: 'italic',
          fontSize: 15,
          color: 'var(--ink-soft)',
          marginTop: 8,
        }}
      >
        Everything here is yours to set. Nothing here will push you.
      </div>

      <div style={label}>Account</div>
      {acct ? (
        <div style={group}>
          <Row icon="you" title={acctTitle!} detail={acctStatus!} />
          {SHOW_IAP && auth.enabled && !auth.isMember && (
            <Row
              icon="sparkle"
              title="Become a member"
              detail="Unlock the full programme"
              onClick={openPaywall}
            />
          )}
          {SHOW_PURCHASE && auth.enabled && !auth.isMember && PURCHASE_URL && (
            <Row
              icon="sparkle"
              title="Become a member"
              detail="Unlock the full programme on our website"
              onClick={() => auth.signUp()}
              control={<span style={{ color: 'var(--ink-soft)', opacity: 0.55 }}><Icon name="external" size={16} /></span>}
            />
          )}
          {auth.enabled && !auth.isMember && (
            <Row
              icon="lock"
              title="Enter an organisation or gift code"
              detail="Unlock the full programme with a code you’ve been given"
              onClick={() => setShowOrgCode(true)}
            />
          )}
          {auth.enabled && !auth.isMember && (
            <Row
              icon="refresh"
              title={auth.checking ? 'Checking…' : 'Refresh membership'}
              detail={SHOW_PURCHASE
                ? 'Just bought on our website? Tap to update your access here.'
                : 'Already a member? Tap to update your access here.'}
              onClick={() => { if (!auth.checking) auth.recheck(); }}
            />
          )}
          {/* One unified subscription row, by how the member pays. Cancelling a store
              subscription happens in the store; a monthly Stripe one is cancelled in-app. */}
          {auth.isMember && subRow && (
            <Row
              icon="settings"
              title={subRow.title}
              detail={subRow.detail}
              onClick={subRow.onClick}
              control={subRow.external ? <span style={{ color: 'var(--ink-soft)', opacity: 0.55 }}><Icon name="external" size={16} /></span> : undefined}
            />
          )}
          {SHOW_PURCHASE && auth.isMember && auth.entitlementSource === 'stripe' && canSync(auth.session) && (
            <Row
              icon="settings"
              title={billingBusy ? 'Opening billing…' : 'Invoices and payment card'}
              detail={billingErr || 'View invoices or update your card, on our payment provider (Stripe)'}
              onClick={onManageBilling}
              control={<span style={{ color: 'var(--ink-soft)', opacity: 0.55 }}><Icon name="external" size={16} /></span>}
            />
          )}
          {/* Native: members whose membership has no subscription row above (manual/trial/other). */}
          {!SHOW_PURCHASE && auth.enabled && auth.isMember && !subRow && (
            <Row
              icon="sparkle"
              title="Membership"
              detail="Your membership is active."
            />
          )}
          <Row
            icon="lock"
            title={confirmSignOut ? 'Tap again to sign out' : 'Sign out'}
            detail={confirmSignOut ? 'Signs you out on this device. Your progress is kept, ready for when you sign back in.' : 'Sign out of your account'}
            danger={confirmSignOut}
            onClick={() => {
              if (confirmSignOut) {
                // Keep this user's data on the device so signing back in restores
                // everything (progress, saved items, moments of practice) with no
                // loss. The next person on a shared device can't see it — they hit
                // the sign-in wall, and signing in as a DIFFERENT account clears the
                // previous user's local data before loading theirs (see useSync).
                auth.signOut();
                setConfirmSignOut(false);
              } else {
                setConfirmSignOut(true);
                window.setTimeout(() => setConfirmSignOut(false), 4000);
              }
            }}
          />
          <Row
            icon="trash"
            title="Delete my account"
            detail="Permanently remove your account and progress"
            danger
            last
            onClick={() => setShowDelete(true)}
          />
        </div>
      ) : (
        <div style={{ ...group, padding: 15 }}>
          <AccountSignIn />
        </div>
      )}

      {a2hsRelevant && (
        <>
          <div style={label}>App</div>
          <div style={group}>
            <Row
              icon="plus"
              title="Add to home screen"
              detail="Open ReNurture full screen from an icon, like an app"
              last
              onClick={() => setShowA2HS(true)}
            />
          </div>
        </>
      )}

      {IN_APP_JOURNALING && (
        <>
          <div style={label}>Journaling</div>
          <div style={group}>
            <Row
              icon="book"
              title="Journal in the app"
              detail={inApp ? 'On — kept on this device' : 'Off — using your own journal (recommended)'}
              last={!inApp}
              control={<Toggle on={inApp} onClick={() => (inApp ? progress.setInAppJournaling(false) : setConfirmInApp(true))} />}
            />
            {inApp && (
              <>
                <Row
                  icon="download"
                  title="Back up my journal to a file"
                  detail={entryCount > 0 ? `${entryCount} ${entryCount === 1 ? 'entry' : 'entries'} · save to your files` : 'Nothing written yet'}
                  onClick={entryCount > 0 ? doExport : undefined}
                />
                <Row
                  icon="plus"
                  title="Import a backup"
                  detail="Restore entries from a backup file on this device"
                  last
                  onClick={() => fileRef.current?.click()}
                />
              </>
            )}
          </div>
          {importMsg && <Meta style={{ display: 'block', margin: '9px 6px 0' }}>{importMsg}</Meta>}
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={onImportFile} style={{ display: 'none' }} />
        </>
      )}

      <div style={label} ref={remindersRef}>Reminders</div>
      {!remindersNotify && (
        <Meta style={{ display: 'block', margin: '-2px 4px 10px' }}>
          Reminders are available in the iOS and Android app. The web version can’t send notifications, so there’s nothing to set here.
        </Meta>
      )}
      {remindersNotify && (<>
      <div style={group}>
        {/* Daily reminder — the main gentle "come back" nudge */}
        <Row
          icon="journey"
          title="Daily reminder"
          detail={daily ? `On · daily at ${daily}` : 'Off'}
          last={!daily}
          control={<Toggle on={!!daily} onClick={() => progress.setMorningAnchor(daily ? null : '19:00')} />}
        />
        {daily && (
          <Row
            icon="clock"
            title="Reminder time"
            detail="When to nudge you each day"
            last
            control={
              <input
                type="time"
                value={daily}
                onChange={(e) => e.target.value && progress.setMorningAnchor(e.target.value)}
                aria-label="Reminder time"
                style={timeInput}
              />
            }
          />
        )}
      </div>

      <div style={group}>
        {/* Calm practice — an optional extra nudge to settle the body */}
        <Row
          icon="leaf"
          title="Calm practice"
          detail={calm ? `On · daily at ${calm}` : 'Off'}
          last={!calm}
          control={<Toggle on={!!calm} onClick={() => progress.setCalmPracticeTime(calm ? null : '08:00')} />}
        />
        {calm && (
          <Row
            icon="clock"
            title="Practice time"
            detail="When to nudge you each day"
            last
            control={
              <input
                type="time"
                value={calm}
                onChange={(e) => e.target.value && progress.setCalmPracticeTime(e.target.value)}
                aria-label="Practice time"
                style={timeInput}
              />
            }
          />
        )}
      </div>
      </>)}

      <div style={label}>Privacy &amp; legal</div>
      <div style={group}>
        {canSync(auth.session) && (
          <Row
            icon="heart"
            title="Help improve the programme"
            detail={
              progress.state.researchConsent === 'granted'
                ? 'Sharing anonymous lesson feedback & Act ratings'
                : 'Anonymous feedback & ratings — off'
            }
            control={
              <Toggle
                on={progress.state.researchConsent === 'granted'}
                onClick={() => {
                  const granting = progress.state.researchConsent !== 'granted';
                  if (granting) {
                    // Don't flip on a bare toggle — re-present the full consent page
                    // and only record consent if they confirm there.
                    setShowReconsent(true);
                    return;
                  }
                  // Turning OFF is an immediate withdrawal of prior consent.
                  progress.setResearchConsent('declined');
                  void logConsent('research', 'withdrawn', CONSENT_VERSIONS.research);
                  // Mirror the withdrawal to the research backend (best-effort).
                  const s = auth.session;
                  if (canSync(s) && s.accessToken) void postResearchConsent(s.accessToken, false);
                }}
              />
            }
          />
        )}
        <Row
          icon="book"
          title="Privacy policy"
          detail="renurture.org/privacy"
          onClick={() => openUrl('https://www.renurture.org/privacy')}
          control={<span style={{ color: 'var(--ink-soft)', opacity: 0.55 }}><Icon name="external" size={16} /></span>}
        />
        <Row
          icon="book"
          title="Terms of use"
          detail="renurture.org/terms"
          onClick={() => openUrl('https://www.renurture.org/terms')}
          control={<span style={{ color: 'var(--ink-soft)', opacity: 0.55 }}><Icon name="external" size={16} /></span>}
        />
        <Row
          icon="tool"
          title="Report a bug"
          detail="Tell us what went wrong - it goes to our support team"
          onClick={() => setShowBug(true)}
          last
        />
      </div>

      {!devUnlocked ? (
        // Collapsed by default: one subtle text button. Tapping it asks for the PIN;
        // only the right PIN unfolds the dev + content-review tools below.
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <button
            type="button"
            onClick={requestUnlock}
            style={{ background: 'none', border: 'none', padding: '10px 14px', minHeight: 44, cursor: 'pointer', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, color: 'var(--ink-soft)', opacity: 0.7 }}
          >
            Dev tools
          </button>
        </div>
      ) : (
        <>
          <div style={label}>Developer</div>
          <div style={group}>
            <Row
              icon="tool"
              title="Developer mode"
              detail={
                progress.state.devMode
                  ? 'On — pacing prompts paused so you can review content'
                  : 'Off — pacing prompts behave normally'
              }
              control={
                <Toggle on={progress.state.devMode} onClick={() => progress.setDevMode(!progress.state.devMode)} />
              }
            />
            <Row
              icon="leaf"
              title="Reset guide tiles"
              detail="Clears “my own way” + caps so every guide can fire again"
              last={!SHOW_IAP}
              onClick={() => progress.resetGuideState()}
            />
            {SHOW_IAP && (
              <Row
                icon="tool"
                title="Store check"
                detail={storeInfo ?? 'Tap to see the store country and prices Apple reports'}
                last
                onClick={() => { setStoreInfo('Checking…'); void storeDiagnostics().then(setStoreInfo); }}
              />
            )}
          </div>

          <div style={label}>Content review</div>
          <div style={group}>
            <Row
              icon="pencil"
              title="Review mode"
              detail={
                progress.state.reviewMode
                  ? 'On — a comment pencil shows on every lesson'
                  : 'Off — comment on content as it’s presented'
              }
              control={<Toggle on={progress.state.reviewMode} onClick={() => progress.setReviewMode(!progress.state.reviewMode)} />}
            />
            <Row
              icon="reflections"
              title="Review notes"
              detail={`${reviewCount} note${reviewCount === 1 ? '' : 's'} · read & export`}
              last
              onClick={onOpenReview}
            />
          </div>

          {progress.state.devMode && (
            <>
              <div style={label}>Research — test</div>
              <div style={group}>
                <Row
                  icon="sparkle"
                  title="Open combined check-in (test)"
                  detail="ONS-4 + GAD-7 + PHQ-8 + WSAS + PSI (ex-interference, research-only). Opens the LIVE check-in for the signed-in account — mints the real pid + gate token, so it exercises the gated save end to end. Needs a signed-in survivor account; each run saves the next wave under that account."
                  last
                  onClick={() => {
                    const s = auth.session;
                    // The check-in page is APP_PAGE_SECRET-gated, so it needs a real
                    // pid + token minted server-side — a fake pid is 404'd. Reuse the
                    // production opener (firstCheckin → demographics) so this truly
                    // tests the gated flow rather than a dead link.
                    if (!canSync(s) || !s.accessToken) return;
                    const consent = progress.state.researchConsent === 'granted';
                    void openResearchCheckin(s.accessToken, undefined, { firstCheckin: true, consent });
                  }}
                  control={<span style={{ color: 'var(--ink-soft)', opacity: 0.55 }}><Icon name="external" size={16} /></span>}
                />
              </div>

              <div style={label}>Guide presence — preview (dev)</div>
              <Meta style={{ display: 'block', margin: '8px 4px 10px' }}>
                All six guide tiles. In normal use these are event-gated (welcome, return, hard day, act end, mastery, check-in).
              </Meta>
              <GuidePreview />
            </>
          )}
        </>
      )}

      <div style={{ textAlign: 'center', marginTop: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'center', opacity: 0.7 }}>
          <Wordmark h={18} />
        </div>
        <Meta style={{ display: 'block', opacity: 0.75, marginTop: 9 }}>
          Made with care · Award-winning, clinician-built
        </Meta>
      </div>
    </div>
    {confirmInApp && (
      <InAppJournalingDialog
        onConfirm={() => { setConfirmInApp(false); progress.setInAppJournaling(true); }}
        onUseOwn={() => { setConfirmInApp(false); progress.setInAppJournaling(false); }}
      />
    )}
    {showDelete && <DeleteAccountDialog onClose={() => setShowDelete(false)} />}
    {showCancel && sessionToken && (
      <CancelSubscriptionDialog
        token={sessionToken}
        periodEnd={acct?.currentPeriodEnd}
        onClose={() => setShowCancel(false)}
        onCancelled={(until) => { statusVersion.current++; setSubStatus({ hasRecurring: true, cancelAtPeriodEnd: true, accessUntil: until }); }}
      />
    )}
    {showBug && <BugReportDialog onClose={() => setShowBug(false)} />}
    {showA2HS && <AddToHomeScreenSheet onClose={() => setShowA2HS(false)} />}
    {showOrgCode && <OrgCodeDialog onClose={() => setShowOrgCode(false)} />}
    {pinOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Enter developer PIN"
        onClick={() => setPinOpen(false)}
        style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(20,14,8,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 320, background: 'var(--cream)', borderRadius: 18, border: '1px solid var(--hair)', boxShadow: '0 18px 50px rgba(20,14,8,0.4)', padding: '22px 20px' }}>
          <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 20, fontWeight: 500, color: 'var(--ink)', marginBottom: 6 }}>
            Enter PIN
          </div>
          <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-soft)', marginBottom: 14 }}>
            The developer and content-review tools are protected. Enter the PIN to open them.
          </div>
          <input
            type="tel"
            inputMode="numeric"
            autoFocus
            value={pinValue}
            onChange={(e) => { setPinValue(e.target.value.replace(/\D/g, '').slice(0, 8)); setPinError(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') submitPin(); }}
            aria-label="PIN"
            placeholder="••••"
            style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 12, border: `1px solid ${pinError ? '#B0413F' : 'var(--hair)'}`, background: 'var(--card-top)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 18, letterSpacing: '0.3em', textAlign: 'center', color: 'var(--ink)' }}
          />
          {pinError && (
            <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, color: '#B0413F', marginTop: 8 }}>That PIN is not right.</div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <Pressable as="button" onClick={() => setPinOpen(false)} ariaLabel="Cancel" style={{ flex: 1, padding: '11px 0', borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </Pressable>
            <Pressable as="button" onClick={submitPin} ariaLabel="Confirm PIN" style={{ flex: 1, padding: '11px 0', borderRadius: 999, border: 'none', background: 'var(--green-deep)', color: '#fff', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Open
            </Pressable>
          </div>
        </div>
      </div>
    )}
    {showReconsent && (
      <ReconsentDialog
        onClose={() => setShowReconsent(false)}
        onDecide={(granted) => {
          if (granted) {
            progress.setResearchConsent('granted', CONSENT_VERSIONS.research);
            void logConsent('research', 'granted', CONSENT_VERSIONS.research);
            // Mirror the grant to the research backend (best-effort).
            const s = auth.session;
            if (canSync(s) && s.accessToken) void postResearchConsent(s.accessToken, true);
          }
          // "Not just now" leaves research off and records nothing.
          setShowReconsent(false);
        }}
      />
    )}
    </>
  );
}
